import React from 'react';
import { ShoppingBagIcon, XMarkIcon } from '@heroicons/react/24/outline';

// Normalize a saved Product Showcase product into the shape this panel
// renders. Saved products store images as an array of { url, alt, isPrimary }
// objects, but older demo data used a flat `image` URL field.
function getProductImage(product) {
  if (!product) return null;
  if (product.image) return product.image;
  if (Array.isArray(product.images) && product.images.length) {
    const primary = product.images.find((i) => i?.isPrimary) || product.images[0];
    return primary?.url || null;
  }
  return null;
}

export default function MerchStorePanel({
  open,
  onClose,
  products = [],
  canControl = false,
  active = false,
  onToggleActive,
  availableShowcases = [],
  selectedShowcaseId = null,
  onSelectShowcase,
}) {
  if (!open) return null;

  const handleAdd = (product) => {
    if (product?.ctaUrl && product.ctaUrl !== '#') {
      window.open(product.ctaUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const showPicker = canControl && availableShowcases.length > 1;
  const hasNoShowcases = canControl && availableShowcases.length === 0;

  return (
    <div
      className="fixed right-4 z-[9999] w-[560px] max-w-[calc(100vw-2rem)] bg-[#0b1020] border border-white/10 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
      style={{ bottom: '96px', maxHeight: 'calc(100vh - 120px)' }}
    >
      <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#6a1fbf] to-[#00c853] flex items-center justify-center">
            <ShoppingBagIcon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-white font-semibold text-sm">Merch Store</h3>
            <p className="text-gray-400 text-xs">Exclusive products</p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded-lg hover:bg-white/5 transition-colors"
          aria-label="Close merch store"
        >
          <XMarkIcon className="w-5 h-5 text-gray-400" />
        </button>
      </div>

      {showPicker && (
        <div className="flex-shrink-0 px-4 py-3 bg-white/[0.03] border-b border-white/10">
          <label className="block text-xs text-gray-400 mb-1.5">Collection</label>
          <select
            value={selectedShowcaseId || ''}
            onChange={(e) => onSelectShowcase?.(e.target.value)}
            className="w-full px-3 py-2 bg-[#0b1020] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-[#6a1fbf]"
          >
            {availableShowcases.map((s) => (
              <option key={s.id} value={s.id} className="bg-[#0b1020]">
                {s.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {canControl && !hasNoShowcases && (
        <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 bg-white/[0.03] border-b border-white/10">
          <div>
            <p className="text-white text-sm font-medium">Show to attendees</p>
            <p className="text-gray-400 text-xs mt-0.5">
              {active
                ? 'Attendees can see the Merch Store button in their bottom bar.'
                : 'Only you can see this. Flip the switch to show it to attendees.'}
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={active}
            onClick={onToggleActive}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
              active ? 'bg-[#00c853]' : 'bg-white/20'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                active ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto">
      {hasNoShowcases ? (
        <div className="p-6 text-center">
          <p className="text-white text-sm font-medium">No Product Showcases yet</p>
          <p className="text-gray-400 text-xs mt-1">
            Create a Product Showcase in the Elements section to use the Merch Store.
          </p>
        </div>
      ) : products.length === 0 ? (
        <div className="p-6 text-center">
          <p className="text-gray-400 text-sm">
            {canControl
              ? 'This collection has no products yet. Add some in the Elements editor.'
              : 'No products available right now.'}
          </p>
        </div>
      ) : (
        <div className="p-4 grid grid-cols-3 gap-3">
          {products.map((product) => {
            const imageUrl = getProductImage(product);
            return (
              <div
                key={product.id || product._id}
                className="bg-black/40 rounded-xl overflow-hidden border border-white/10 flex flex-col"
              >
                <div className="aspect-[4/5] bg-gradient-to-br from-gray-800 to-gray-900 overflow-hidden">
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={product.name}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : null}
                </div>
                <div className="p-3 flex flex-col flex-1">
                  <p className="text-white font-medium text-sm leading-tight">{product.name}</p>
                  <p className="text-[#00c853] font-bold mt-1">
                    {product.currency ? `${product.currency} ` : '$'}{product.price}
                  </p>
                  <button
                    type="button"
                    onClick={() => handleAdd(product)}
                    className="mt-3 w-full py-2 bg-[#6a1fbf] hover:bg-[#7a2ecf] transition-colors rounded-lg text-white font-medium text-sm"
                    disabled={!product.ctaUrl || product.ctaUrl === '#'}
                  >
                    {product.ctaText || 'Add'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      </div>
    </div>
  );
}
